<template>
  <div class="mt-16">
    <VueHighcharts
      v-if="chartDepartureCost && chartDepartureBudgetHours && !refreshChart"
      type="chart"
      :redrawOnUpdate="true"
      :oneToOneUpdate="true"
      :animateOnUpdate="true"
      :options="chartBarDepartureWorkHoursOptions"
    />
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps, ref, watch, nextTick } from "vue";
import VueHighcharts from "vue3-highcharts";
import { useStore } from "vuex";

const emit = defineEmits(["update:loading"]);

const props = defineProps({
  dateFrom: {
    type: String,
  },
  dateTo: {
    type: String,
  },
  departuresKey: {
    type: Array,
  },
  isAccumulative: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: true,
  },
});

const store = useStore();

const chartDepartureCost = ref([]);
const chartDepartureBudgetHours = ref([]);
const refreshChart = ref(false);

const getChartColors = computed(() => store.getters.getChartColors);
const getDepartureWorkHourReport = computed(
  () => store.getters.getDepartureWorkHourReport
);

const chartBarDepartureWorkHoursOptions = computed(() => {
  return {
    colors: getChartColors.value,
    chart: {
      type: "column",
    },
    title: {
      text: "HH real vs HH presupuesto",
    },
    xAxis: {
      type: "category",
    },
    yAxis: {
      min: 0,
      title: {
        text: "HH",
      },
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
      pointFormat:
        '<tr><td style="color:{series.color};padding:0">{series.name}: </td>' +
        '<td style="padding:0"><b>{point.y:.1f} HH</b></td></tr>',
      footerFormat: "</table>",
      shared: true,
      useHTML: true,
    },
    plotOptions: {
      column: {
        pointPadding: 0,
        borderWidth: 0,
      },
    },
    series: [
      {
        name: "HH real",
        data: chartDepartureCost.value,
        // dataSorting: {
        //   enabled: true,
        //   matchByName: true
        // },
        dataLabels: {
          enabled: true,
          // rotation: -90,
          color: "#FFFFFF",
          // align: "right",
          format: "{point.y:.1f} HH",
          y: 10, // 10 pixels down from the top
          style: {
            fontSize: "13px",
            fontFamily: "Verdana, sans-serif",
          },
        },
      },
      {
        name: "HH presupuesto",
        data: chartDepartureBudgetHours.value,
        dataLabels: {
          enabled: true,
          // rotation: -90,
          color: "#FFFFFF",
          // align: "right",
          format: "{point.y:.1f} HH",
          y: 10, // 10 pixels down from the top
          style: {
            fontSize: "13px",
            fontFamily: "Verdana, sans-serif",
          },
        },
      },
    ],
  };
});

const getShowMenu = computed(() => store.getters.getShowMenu);

watch(
  () => getShowMenu.value,
  () => {
    refreshChart.value = true;
    nextTick(() => {
      refreshChart.value = false;
    });
  }
);

watch(
  () => getDepartureWorkHourReport.value,
  (currentValue) => {
    let groupBy = function (xs, key) {
      return xs.reduce(function (rv, x) {
        rv[x[key]] = [
          x.budgetHours,
          (rv[x[key]] ? rv[x[key]][1] : 0) + x.hoursWorked,
        ];
        return rv;
      }, {});
    };

    const departures = groupBy(currentValue, "name");
    let departureBudget = [];
    let departureCost = [];

    for (const d in departures) {
      departureBudget.push([d, departures[d][0]]);
      departureCost.push([d, departures[d][1]]);
    }

    chartDepartureCost.value = departureCost;
    chartDepartureBudgetHours.value = departureBudget;
  }
);

const loadData = async () => {
  try {
    emit("update:loading", true);

    const filter = {
      dateFrom: props.dateFrom,
      departuresKey: props.departuresKey || [],
    };
    if (props.dateTo) filter.dateTo = props.dateTo;

    await store.dispatch("loadDepartureWorkHourReport", filter);
  } catch (error) {
    console.error(error);
  } finally {
    emit("update:loading", false);
  }
};

watch(
  () => props.dateFrom,
  () => loadData()
);

watch(
  () => props.departuresKey,
  () => {
    if (props.isAccumulative) return;
    loadData();
  },
  { deep: true }
);

const init = async () => {
  await loadData();
};

init();
</script>
